import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    home: {
      greeting: "Hello Darling!",
      subtitle: "I'm Sophia the Wise",
      tagline: "Old Soul \u00B7 Loud Glasses \u00B7 Calm Truth",
      conversation: "Let's Talk",
      conversationDesc: "Have a conversation with me",
      advice: "Quick Advice",
      adviceDesc: "Get a witty one-liner",
      dailyAdvice: "Daily Wisdom",
      dailyAdviceDesc: "Your daily dose of Sophia",
      photoWithSophia: "Photo with Me",
      photoDesc: "Create a stylish image together",
    },
    conversation: {
      title: "Conversation",
      placeholder: "Tell me what's on your mind...",
      send: "Send",
      listening: "I'm listening...",
      speaking: "Speaking...",
      tapToSpeak: "Tap to speak",
      newChat: "New Conversation",
      back: "Back",
      relisten: "Listen again",
    },
    advice: {
      title: "Quick Advice",
      placeholder: "Tell me about your day...",
      listening: "I'm listening, darling...",
      getAdvice: "Ask Sophia",
      back: "Back",
    },
    daily: {
      title: "Daily Wisdom",
      settings: "Settings",
      enabled: "Daily messages enabled",
      disabled: "Daily messages disabled",
      messageType: "Message type",
      voice: "Voice",
      text: "Text",
      time: "Delivery time",
      save: "Save Settings",
      todayMessage: "Today's Wisdom",
      getWisdom: "Get Today's Wisdom",
      listen: "Listen",
      share: "Share",
      back: "Back",
    },
    photo: {
      title: "Photo with Sophia",
      upload: "Upload your photo",
      uploadDesc: "Drop an image or click to browse",
      subjectType: "What's in your photo?",
      person: "A Person",
      pet: "A Pet",
      scene: "Choose a scene",
      scenes: {
        beach: "Beach",
        livingRoom: "Living Room",
        elegant: "Elegant Setting",
        casual: "Casual Moment",
        garden: "Garden",
        cafe: "Cozy Cafe",
        other: "Other",
      },
      otherPlaceholder: "Describe your dream scene...",
      generate: "Create Photo",
      generating: "Sophia is creating your photo...",
      download: "Download",
      share: "Share",
      back: "Back",
    },
    language: "Language",
  },
  de: {
    home: {
      greeting: "Hallo Liebling!",
      subtitle: "Ich bin Sophia die Weise",
      tagline: "Alte Seele \u00B7 Laute Brille \u00B7 Stille Wahrheit",
      conversation: "Lass uns reden",
      conversationDesc: "Unterhalte dich mit mir",
      advice: "Schneller Rat",
      adviceDesc: "Bekomme einen witzigen Einzeiler",
      dailyAdvice: "Tägliche Weisheit",
      dailyAdviceDesc: "Deine tägliche Dosis Sophia",
      photoWithSophia: "Foto mit mir",
      photoDesc: "Erstelle ein stilvolles Bild zusammen",
    },
    conversation: {
      title: "Gespräch",
      placeholder: "Erzähl mir, was dich beschäftigt...",
      send: "Senden",
      listening: "Ich höre zu...",
      speaking: "Ich spreche...",
      tapToSpeak: "Tippe zum Sprechen",
      newChat: "Neues Gespräch",
      back: "Zurück",
      relisten: "Nochmal hören",
    },
    advice: {
      title: "Schneller Rat",
      placeholder: "Erzähl mir von deinem Tag...",
      listening: "Ich höre zu, Liebling...",
      getAdvice: "Frag Sophia",
      back: "Zurück",
    },
    daily: {
      title: "Tägliche Weisheit",
      settings: "Einstellungen",
      enabled: "Tägliche Nachrichten aktiviert",
      disabled: "Tägliche Nachrichten deaktiviert",
      messageType: "Nachrichtentyp",
      voice: "Sprache",
      text: "Text",
      time: "Lieferzeit",
      save: "Einstellungen speichern",
      todayMessage: "Heutige Weisheit",
      getWisdom: "Heutige Weisheit abrufen",
      listen: "Anhören",
      share: "Teilen",
      back: "Zurück",
    },
    photo: {
      title: "Foto mit Sophia",
      upload: "Lade dein Foto hoch",
      uploadDesc: "Ziehe ein Bild hierher oder klicke zum Durchsuchen",
      subjectType: "Was ist auf deinem Foto?",
      person: "Eine Person",
      pet: "Ein Haustier",
      scene: "Wähle eine Szene",
      scenes: {
        beach: "Strand",
        livingRoom: "Wohnzimmer",
        elegant: "Elegantes Setting",
        casual: "Lässiger Moment",
        garden: "Garten",
        cafe: "Gemütliches Café",
        other: "Anderes",
      },
      otherPlaceholder: "Beschreibe deine Traumszene...",
      generate: "Foto erstellen",
      generating: "Sophia erstellt dein Foto...",
      download: "Herunterladen",
      share: "Teilen",
      back: "Zurück",
    },
    language: "Sprache",
  },
  es: {
    home: {
      greeting: "¡Hola Cariño!",
      subtitle: "Soy Sophia la Sabia",
      tagline: "Alma Vieja \u00B7 Gafas Atrevidas \u00B7 Verdad Serena",
      conversation: "Hablemos",
      conversationDesc: "Conversa conmigo",
      advice: "Consejo Rápido",
      adviceDesc: "Obtén una frase ingeniosa",
      dailyAdvice: "Sabiduría Diaria",
      dailyAdviceDesc: "Tu dosis diaria de Sophia",
      photoWithSophia: "Foto Conmigo",
      photoDesc: "Crea una imagen elegante juntos",
    },
    conversation: {
      title: "Conversación",
      placeholder: "Cuéntame qué tienes en mente...",
      send: "Enviar",
      listening: "Te escucho...",
      speaking: "Hablando...",
      tapToSpeak: "Toca para hablar",
      newChat: "Nueva Conversación",
      back: "Atrás",
      relisten: "Escuchar de nuevo",
    },
    advice: {
      title: "Consejo Rápido",
      placeholder: "Cuéntame sobre tu día...",
      listening: "Te escucho, cariño...",
      getAdvice: "Pregunta a Sophia",
      back: "Atrás",
    },
    daily: {
      title: "Sabiduría Diaria",
      settings: "Configuración",
      enabled: "Mensajes diarios activados",
      disabled: "Mensajes diarios desactivados",
      messageType: "Tipo de mensaje",
      voice: "Voz",
      text: "Texto",
      time: "Hora de entrega",
      save: "Guardar Configuración",
      todayMessage: "Sabiduría de Hoy",
      getWisdom: "Obtener Sabiduría de Hoy",
      listen: "Escuchar",
      share: "Compartir",
      back: "Atrás",
    },
    photo: {
      title: "Foto con Sophia",
      upload: "Sube tu foto",
      uploadDesc: "Arrastra una imagen o haz clic para buscar",
      subjectType: "¿Qué hay en tu foto?",
      person: "Una Persona",
      pet: "Una Mascota",
      scene: "Elige una escena",
      scenes: {
        beach: "Playa",
        livingRoom: "Sala de Estar",
        elegant: "Escenario Elegante",
        casual: "Momento Casual",
        garden: "Jardín",
        cafe: "Café Acogedor",
        other: "Otro",
      },
      otherPlaceholder: "Describe tu escena soñada...",
      generate: "Crear Foto",
      generating: "Sophia está creando tu foto...",
      download: "Descargar",
      share: "Compartir",
      back: "Atrás",
    },
    language: "Idioma",
  },
  it: {
    home: {
      greeting: "Ciao Tesoro!",
      subtitle: "Sono Sophia la Saggia",
      tagline: "Anima Antica \u00B7 Occhiali Audaci \u00B7 Verità Calma",
      conversation: "Parliamo",
      conversationDesc: "Fai una conversazione con me",
      advice: "Consiglio Rapido",
      adviceDesc: "Ottieni una battuta spiritosa",
      dailyAdvice: "Saggezza Quotidiana",
      dailyAdviceDesc: "La tua dose quotidiana di Sophia",
      photoWithSophia: "Foto con Me",
      photoDesc: "Crea un'immagine elegante insieme",
    },
    conversation: {
      title: "Conversazione",
      placeholder: "Dimmi cosa hai in mente...",
      send: "Invia",
      listening: "Ti ascolto...",
      speaking: "Sto parlando...",
      tapToSpeak: "Tocca per parlare",
      newChat: "Nuova Conversazione",
      back: "Indietro",
      relisten: "Riascolta",
    },
    advice: {
      title: "Consiglio Rapido",
      placeholder: "Raccontami della tua giornata...",
      listening: "Ti ascolto, tesoro...",
      getAdvice: "Chiedi a Sophia",
      back: "Indietro",
    },
    daily: {
      title: "Saggezza Quotidiana",
      settings: "Impostazioni",
      enabled: "Messaggi giornalieri attivati",
      disabled: "Messaggi giornalieri disattivati",
      messageType: "Tipo di messaggio",
      voice: "Voce",
      text: "Testo",
      time: "Ora di consegna",
      save: "Salva Impostazioni",
      todayMessage: "Saggezza di Oggi",
      getWisdom: "Ottieni Saggezza di Oggi",
      listen: "Ascolta",
      share: "Condividi",
      back: "Indietro",
    },
    photo: {
      title: "Foto con Sophia",
      upload: "Carica la tua foto",
      uploadDesc: "Trascina un'immagine o clicca per cercare",
      subjectType: "Cosa c'è nella tua foto?",
      person: "Una Persona",
      pet: "Un Animale",
      scene: "Scegli una scena",
      scenes: {
        beach: "Spiaggia",
        livingRoom: "Soggiorno",
        elegant: "Ambientazione Elegante",
        casual: "Momento Casual",
        garden: "Giardino",
        cafe: "Caffè Accogliente",
        other: "Altro",
      },
      otherPlaceholder: "Descrivi la tua scena dei sogni...",
      generate: "Crea Foto",
      generating: "Sophia sta creando la tua foto...",
      download: "Scarica",
      share: "Condividi",
      back: "Indietro",
    },
    language: "Lingua",
  },
  fr: {
    home: {
      greeting: "Bonjour Chéri!",
      subtitle: "Je suis Sophia la Sage",
      tagline: "Âme Ancienne \u00B7 Lunettes Audacieuses \u00B7 Vérité Calme",
      conversation: "Parlons",
      conversationDesc: "Avoir une conversation avec moi",
      advice: "Conseil Rapide",
      adviceDesc: "Obtenir une réplique spirituelle",
      dailyAdvice: "Sagesse du Jour",
      dailyAdviceDesc: "Votre dose quotidienne de Sophia",
      photoWithSophia: "Photo avec Moi",
      photoDesc: "Créer une image élégante ensemble",
    },
    conversation: {
      title: "Conversation",
      placeholder: "Dites-moi ce qui vous préoccupe...",
      send: "Envoyer",
      listening: "Je vous écoute...",
      speaking: "Je parle...",
      tapToSpeak: "Appuyez pour parler",
      newChat: "Nouvelle Conversation",
      back: "Retour",
      relisten: "Réécouter",
    },
    advice: {
      title: "Conseil Rapide",
      placeholder: "Parlez-moi de votre journée...",
      listening: "Je vous écoute, chéri...",
      getAdvice: "Demander à Sophia",
      back: "Retour",
    },
    daily: {
      title: "Sagesse du Jour",
      settings: "Paramètres",
      enabled: "Messages quotidiens activés",
      disabled: "Messages quotidiens désactivés",
      messageType: "Type de message",
      voice: "Voix",
      text: "Texte",
      time: "Heure de livraison",
      save: "Enregistrer",
      todayMessage: "Sagesse d'Aujourd'hui",
      getWisdom: "Obtenir la Sagesse du Jour",
      listen: "Écouter",
      share: "Partager",
      back: "Retour",
    },
    photo: {
      title: "Photo avec Sophia",
      upload: "Téléchargez votre photo",
      uploadDesc: "Déposez une image ou cliquez pour parcourir",
      subjectType: "Qu'y a-t-il sur votre photo?",
      person: "Une Personne",
      pet: "Un Animal",
      scene: "Choisir une scène",
      scenes: {
        beach: "Plage",
        livingRoom: "Salon",
        elegant: "Cadre Élégant",
        casual: "Moment Décontracté",
        garden: "Jardin",
        cafe: "Café Cosy",
        other: "Autre",
      },
      otherPlaceholder: "Décrivez votre scène de rêve...",
      generate: "Créer Photo",
      generating: "Sophia crée votre photo...",
      download: "Télécharger",
      share: "Partager",
      back: "Retour",
    },
    language: "Langue",
  },
  ja: {
    home: {
      greeting: "こんにちは、ダーリン！",
      subtitle: "私はソフィア・ザ・ワイズ",
      tagline: "古い魂 \u00B7 派手なメガネ \u00B7 穏やかな真実",
      conversation: "お話ししましょう",
      conversationDesc: "私と会話してください",
      advice: "クイックアドバイス",
      adviceDesc: "ウィットに富んだ一言を",
      dailyAdvice: "今日の知恵",
      dailyAdviceDesc: "毎日のソフィアからの言葉",
      photoWithSophia: "私との写真",
      photoDesc: "一緒におしゃれな画像を作成",
    },
    conversation: {
      title: "会話",
      placeholder: "何を考えているか教えて...",
      send: "送信",
      listening: "聞いています...",
      speaking: "話しています...",
      tapToSpeak: "タップして話す",
      newChat: "新しい会話",
      back: "戻る",
      relisten: "もう一度聞く",
    },
    advice: {
      title: "クイックアドバイス",
      placeholder: "今日のことを教えて...",
      listening: "聞いているわ、ダーリン...",
      getAdvice: "ソフィアに聞く",
      back: "戻る",
    },
    daily: {
      title: "今日の知恵",
      settings: "設定",
      enabled: "毎日のメッセージ有効",
      disabled: "毎日のメッセージ無効",
      messageType: "メッセージタイプ",
      voice: "音声",
      text: "テキスト",
      time: "配信時間",
      save: "設定を保存",
      todayMessage: "今日の知恵",
      getWisdom: "今日の知恵を取得",
      listen: "聞く",
      share: "シェア",
      back: "戻る",
    },
    photo: {
      title: "ソフィアとの写真",
      upload: "写真をアップロード",
      uploadDesc: "画像をドロップまたはクリックして選択",
      subjectType: "写真には何が写っていますか？",
      person: "人物",
      pet: "ペット",
      scene: "シーンを選択",
      scenes: {
        beach: "ビーチ",
        livingRoom: "リビングルーム",
        elegant: "エレガントな設定",
        casual: "カジュアルな瞬間",
        garden: "庭園",
        cafe: "居心地の良いカフェ",
        other: "その他",
      },
      otherPlaceholder: "夢のシーンを描写...",
      generate: "写真を作成",
      generating: "ソフィアが写真を作成中...",
      download: "ダウンロード",
      share: "シェア",
      back: "戻る",
    },
    language: "言語",
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('en');

  const t = useCallback(
    (path) => {
      const keys = path.split('.');
      let result = translations[language];
      for (const key of keys) {
        result = result?.[key];
      }
      return result || path;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
